/*
  Lightbox Fibrolab (product-modal) — reemplaza el visor stock de Dawn.
  Markup: snippets/product-media-modal.liquid · Estilos: assets/fibrolab-lightbox.css

  Hereda de ModalDialog (global.js): [open], overflow-hidden, trapFocus, ESC y
  el click del botón ModalClose-*. El swap de variantes (product-info.js)
  reemplaza el innerHTML del viewport → los listeners van delegados en el
  viewport/raíz y los slides se re-consultan en cada show().
*/
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.viewport = this.querySelector('[data-fb-viewport]');
        this.dialog = this.querySelector('.fb-lightbox__dialog');
        this.counterEl = this.querySelector('.fb-lightbox__counter');
        this.counterCurrent = this.querySelector('[data-fb-counter-current]');
        this.counterTotal = this.querySelector('[data-fb-counter-total]');
        this.navPrev = this.querySelector('[data-fb-prev]');
        this.navNext = this.querySelector('[data-fb-next]');
        this.statusEl = this.querySelector('[data-fb-status]');

        this.slides = [];
        this.index = 0;
        this.io = null;
        this.zoom = { el: null, scale: 1, tx: 0, ty: 0, baseW: 0, baseH: 0 };
        this.gesture = {
          mode: 'idle', // idle | pinch | pan | dismiss
          pointers: new Map(),
          startDist: 1,
          startScale: 1,
          startTx: 0,
          startTy: 0,
          startMid: { x: 0, y: 0 },
          center: { x: 0, y: 0 },
          sx: 0,
          sy: 0,
          st: 0,
          moved: false,
          lastTap: { t: 0, x: 0, y: 0 },
          raf: 0,
        };
        this.onResizeBound = this.onResize.bind(this);

        if (!this.viewport) return; // markup inesperado: queda el ModalDialog básico

        this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.viewport.addEventListener('pointercancel', this.onPointerCancel.bind(this));

        // Candados iOS: el pinch es nuestro, nunca zoom de página ni scroll nativo con zoom activo
        this.viewport.addEventListener(
          'touchmove',
          (event) => {
            if (this.gesture.pointers.size >= 2 || this.zoom.scale > 1 || this.gesture.mode === 'dismiss') {
              event.preventDefault();
            }
          },
          { passive: false }
        );
        ['gesturestart', 'gesturechange', 'gestureend'].forEach((name) =>
          this.addEventListener(name, (event) => event.preventDefault())
        );

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-fb-prev]')) this.goTo(this.index - 1);
          else if (event.target.closest('[data-fb-next]')) this.goTo(this.index + 1);
        });
        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowLeft') this.goTo(this.index - 1);
          if (event.code === 'ArrowRight') this.goTo(this.index + 1);
        });
      }

      /* ---------- ciclo de vida ---------- */

      show(opener) {
        super.show(opener);
        if (!this.viewport) return;
        this.refreshSlides();
        const mediaId = opener ? opener.getAttribute('data-media-id') : null;
        let start = this.slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
        if (start < 0) start = 0;
        this.index = start;
        this.updateUI();
        requestAnimationFrame(() => {
          this.viewport.scrollTo({ left: start * this.viewport.clientWidth, behavior: 'instant' });
          this.connectObserver();
        });
        // paridad con Dawn: los videos externos de YouTube se cargan al abrir sobre ellos
        const deferred = this.slides[start] && this.slides[start].querySelector('deferred-media');
        const template = deferred && deferred.querySelector('template');
        if (deferred && template && template.content.querySelector('.js-youtube')) deferred.loadContent();
        this.preload(start);
        window.addEventListener('resize', this.onResizeBound);
      }

      hide() {
        if (this.viewport) {
          this.resetZoom(false);
          if (this.dialog) {
            this.dialog.style.transform = '';
            this.dialog.classList.remove('is-returning');
          }
          this.style.opacity = '';
          if (this.io) {
            this.io.disconnect();
            this.io = null;
          }
          this.gesture.pointers.clear();
          this.gesture.mode = 'idle';
          window.removeEventListener('resize', this.onResizeBound);
        }
        super.hide();
      }

      refreshSlides() {
        this.slides = Array.from(this.querySelectorAll('[data-fb-slide]'));
        const single = this.slides.length < 2;
        if (this.counterTotal) this.counterTotal.textContent = String(this.slides.length);
        if (this.counterEl) this.counterEl.toggleAttribute('hidden', single);
        if (this.navPrev) this.navPrev.toggleAttribute('hidden', single);
        if (this.navNext) this.navNext.toggleAttribute('hidden', single);
      }

      connectObserver() {
        if (this.io) this.io.disconnect();
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const i = this.slides.indexOf(entry.target);
              if (i > -1 && i !== this.index) this.onSlideChange(i);
            });
          },
          { root: this.viewport, threshold: 0.55 }
        );
        this.slides.forEach((slide) => this.io.observe(slide));
      }

      onSlideChange(i) {
        this.resetZoom(false);
        window.pauseAllMedia();
        this.index = i;
        this.updateUI();
        this.preload(i);
      }

      updateUI() {
        if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
        if (this.navPrev) this.navPrev.disabled = this.index === 0;
        if (this.navNext) this.navNext.disabled = this.index >= this.slides.length - 1;
        if (this.statusEl) this.statusEl.textContent = 'Imagen ' + (this.index + 1) + ' de ' + this.slides.length;
      }

      preload(i) {
        [i - 1, i + 1].forEach((n) => {
          const slide = this.slides[n];
          if (slide) slide.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        });
      }

      goTo(i) {
        if (!this.slides.length) return;
        const target = Math.max(0, Math.min(this.slides.length - 1, i));
        if (target === this.index) return;
        this.resetZoom(false);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.viewport.scrollTo({ left: target * this.viewport.clientWidth, behavior: reduce ? 'instant' : 'smooth' });
      }

      onResize() {
        if (!this.hasAttribute('open')) return;
        this.resetZoom(false);
        this.viewport.scrollTo({ left: this.index * this.viewport.clientWidth, behavior: 'instant' });
      }

      /* ---------- gestos ---------- */

      onPointerDown(event) {
        if (event.target.closest('button, a, deferred-media, product-model')) return;
        const g = this.gesture;
        g.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (g.pointers.size === 1) {
          g.sx = event.clientX;
          g.sy = event.clientY;
          g.st = Date.now();
          g.startTx = this.zoom.tx;
          g.startTy = this.zoom.ty;
          g.moved = false;
          g.mode = 'idle';
        } else if (g.pointers.size === 2) {
          const zoomEl = this.activeZoomEl();
          if (!zoomEl || !this.beginZoomSession(zoomEl)) return;
          const pts = Array.from(g.pointers.values());
          g.mode = 'pinch';
          g.startDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
          g.startScale = this.zoom.scale;
          g.startTx = this.zoom.tx;
          g.startTy = this.zoom.ty;
          g.startMid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
          const rect = this.slides[this.index].getBoundingClientRect();
          g.center = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
          try {
            this.viewport.setPointerCapture(event.pointerId);
          } catch (e) {
            /* noop */
          }
        }
      }

      onPointerMove(event) {
        const g = this.gesture;
        if (!g.pointers.has(event.pointerId)) return;
        g.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (g.mode === 'pinch' && g.pointers.size >= 2) {
          const pts = Array.from(g.pointers.values());
          const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
          const scale = Math.min(3, Math.max(1, (g.startScale * dist) / g.startDist));
          const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
          // el punto de contenido bajo el pellizco se mantiene bajo los dedos
          const d0x = (g.startMid.x - g.center.x - g.startTx) / g.startScale;
          const d0y = (g.startMid.y - g.center.y - g.startTy) / g.startScale;
          this.zoom.scale = scale;
          this.zoom.tx = mid.x - g.center.x - d0x * scale;
          this.zoom.ty = mid.y - g.center.y - d0y * scale;
          this.clampPan();
          this.requestApply();
          return;
        }

        const dx = event.clientX - g.sx;
        const dy = event.clientY - g.sy;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.moved = true;

        if (this.zoom.scale > 1 && g.pointers.size === 1) {
          g.mode = 'pan';
          this.zoom.tx = g.startTx + dx;
          this.zoom.ty = g.startTy + dy;
          this.clampPan();
          this.requestApply();
          return;
        }

        if (g.mode === 'idle' && dy > 12 && dy > 1.2 * Math.abs(dx)) {
          g.mode = 'dismiss';
          try {
            this.viewport.setPointerCapture(event.pointerId);
          } catch (e) {
            /* noop */
          }
        }
        if (g.mode === 'dismiss' && this.dialog) {
          const offset = Math.max(0, dy);
          this.dialog.style.transform = 'translateY(' + offset + 'px)';
          this.style.opacity = String(1 - Math.min(offset / 600, 0.4));
        }
      }

      onPointerUp(event) {
        const g = this.gesture;
        if (!g.pointers.has(event.pointerId)) return;
        g.pointers.delete(event.pointerId);

        if (g.mode === 'pinch') {
          if (g.pointers.size < 2) {
            if (this.zoom.scale < 1.1) this.resetZoom(true);
            if (g.pointers.size === 1) {
              const rest = Array.from(g.pointers.values())[0];
              g.sx = rest.x;
              g.sy = rest.y;
              g.startTx = this.zoom.tx;
              g.startTy = this.zoom.ty;
              g.mode = this.zoom.scale > 1 ? 'pan' : 'idle';
            } else {
              g.mode = 'idle';
            }
          }
          return;
        }

        if (g.mode === 'dismiss') {
          const dy = event.clientY - g.sy;
          const dt = Math.max(1, Date.now() - g.st);
          if (dy > 120 || dy / dt > 0.5) {
            this.hide();
          } else {
            this.springBack();
          }
          g.mode = 'idle';
          return;
        }

        if (g.mode === 'pan') {
          g.mode = 'idle';
          return;
        }

        if (!g.moved) this.handleTap(event);
        g.mode = 'idle';
      }

      onPointerCancel(event) {
        const g = this.gesture;
        g.pointers.delete(event.pointerId);
        if (g.mode === 'dismiss') this.springBack();
        if (g.pointers.size === 0) g.mode = 'idle';
      }

      springBack() {
        if (!this.dialog) return;
        this.dialog.classList.add('is-returning');
        this.dialog.style.transform = '';
        this.style.opacity = '';
        setTimeout(() => this.dialog.classList.remove('is-returning'), 220);
      }

      handleTap(event) {
        const slide = this.slides[this.index];
        if (!slide) return;
        const g = this.gesture;
        const now = Date.now();
        const zoomEl = slide.querySelector('[data-fb-zoom]');
        const img = zoomEl ? zoomEl.querySelector('img') : null;
        const overImage = img
          ? this.pointInRect(event.clientX, event.clientY, img.getBoundingClientRect())
          : false;

        const isDouble =
          now - g.lastTap.t < 300 &&
          Math.hypot(event.clientX - g.lastTap.x, event.clientY - g.lastTap.y) < 24;
        g.lastTap = isDouble ? { t: 0, x: 0, y: 0 } : { t: now, x: event.clientX, y: event.clientY };

        if (isDouble) {
          if (overImage || this.zoom.scale > 1) this.toggleZoom(event.clientX, event.clientY);
          return;
        }

        if (!overImage) {
          if (this.zoom.scale > 1) {
            this.resetZoom(true);
          } else if (!event.target.closest('deferred-media, product-model')) {
            this.hide(); // tap/click en el fondo oscuro
          }
        }
      }

      /* ---------- zoom ---------- */

      activeZoomEl() {
        const slide = this.slides[this.index];
        return slide ? slide.querySelector('[data-fb-zoom]') : null;
      }

      beginZoomSession(zoomEl) {
        if (this.zoom.el && this.zoom.el !== zoomEl) this.resetZoom(false);
        const img = zoomEl.querySelector('img');
        if (!img) return false;
        this.zoom.el = zoomEl;
        const rect = img.getBoundingClientRect();
        this.zoom.baseW = rect.width / this.zoom.scale;
        this.zoom.baseH = rect.height / this.zoom.scale;
        return true;
      }

      toggleZoom(x, y) {
        const slide = this.slides[this.index];
        const zoomEl = slide ? slide.querySelector('[data-fb-zoom]') : null;
        if (!zoomEl) return;
        if (this.zoom.scale > 1) {
          this.resetZoom(true);
          return;
        }
        if (!this.beginZoomSession(zoomEl)) return;
        const rect = slide.getBoundingClientRect();
        const target = 2.5;
        this.zoom.scale = target;
        this.zoom.tx = (rect.left + rect.width / 2 - x) * (target - 1);
        this.zoom.ty = (rect.top + rect.height / 2 - y) * (target - 1);
        this.clampPan();
        this.animateApply();
      }

      clampPan() {
        const slide = this.slides[this.index];
        if (!slide) return;
        const maxX = Math.max(0, (this.zoom.baseW * this.zoom.scale - slide.clientWidth) / 2);
        const maxY = Math.max(0, (this.zoom.baseH * this.zoom.scale - slide.clientHeight) / 2);
        this.zoom.tx = Math.min(maxX, Math.max(-maxX, this.zoom.tx));
        this.zoom.ty = Math.min(maxY, Math.max(-maxY, this.zoom.ty));
      }

      applyTransform() {
        if (!this.zoom.el) return;
        const zoomed = this.zoom.scale > 1;
        this.zoom.el.style.transform =
          zoomed || this.zoom.tx || this.zoom.ty
            ? 'translate3d(' + this.zoom.tx + 'px, ' + this.zoom.ty + 'px, 0) scale(' + this.zoom.scale + ')'
            : '';
        this.zoom.el.classList.toggle('is-zoomed', zoomed);
        this.classList.toggle('fb-lightbox--zoomed', zoomed);
      }

      requestApply() {
        if (this.gesture.raf) return;
        this.gesture.raf = requestAnimationFrame(() => {
          this.gesture.raf = 0;
          this.applyTransform();
        });
      }

      animateApply() {
        if (!this.zoom.el) return;
        const el = this.zoom.el;
        el.classList.add('is-animating');
        this.applyTransform();
        setTimeout(() => el.classList.remove('is-animating'), 280);
      }

      resetZoom(animated) {
        this.zoom.scale = 1;
        this.zoom.tx = 0;
        this.zoom.ty = 0;
        if (!this.zoom.el) {
          this.classList.remove('fb-lightbox--zoomed');
          return;
        }
        if (animated) this.animateApply();
        else this.applyTransform();
      }

      pointInRect(x, y, rect) {
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      }
    }
  );
}
